import { useCallback, useEffect, useMemo, useState, memo } from 'react';
import { Table, Typography, Pagination } from 'antd';
import { DownloadOutlined } from '@ant-design/icons';
import clsx from 'clsx';
import { useDispatch, useSelector } from 'react-redux';
import {
  selectAllAbsencesFlat,
  selectCalendarDateFilter,
  selectCalendarEmployees,
  selectCalendarWithAbsencesOnly,
  selectDatesArrayForCurrentMonth,
  selectDatesArrayForCurrentYear,
  selectEmployeesWithAbsencesOnly,
  selectEmployeesWithAbsences,
  selectYearCalendarEmployee,
  selectCalendarYear,
  selectMonthYearToggleState,
  selectCalendarEmployeeState,
  selectCalendarBranchFilter,
  selectCalendarPagination,
} from 'modules/SalaryModule/Calendar/selectors';
import {
  setMonthYearToggleValue,
  setCalendarEmployeeValue,
  setCalendarDatePickerValue,
  setCalendarWithAbsencesOnly,
  downloadAbsences,
  setCalendarPage,
} from 'modules/SalaryModule/Calendar/actions';
import { fetchEmployeesRequest } from 'modules/SalaryModule/Employees/actions';
import { useHolidays } from 'modules/SalaryModule/Calendar/utils';
import {
  formatDatesToFilter,
  formatEmployeeToLeaveModal,
  mapAbsencesToDates,
} from 'modules/SalaryModule/Calendar/helpers';
import { SALARY_CALENDAR_TRANSLATIONS } from 'const/translations/SalaryCalendar';
import { selectUserCurrentCompany } from 'modules/loggedUserInfo/selectors';
import { useSimpleMedia } from 'hooks/useMedia';
import { useGetCalendarMonthViewColumn } from 'hooks/useGetCalendarMonthViewColumn';
import { useGetCalendarYearViewColumn } from 'hooks/useGetCalendarYearViewColumn';

import { STATUS_LOADING, STATUS_NOT_REQUESTED } from 'const';
import { HOMEPAGE_FORM_TRANSLATIONS } from 'const/translations';
import { CalendarFilters } from 'components/SalaryComponents/Calendar/CalendarFilters';
import { getScreenWidthBreakpointMedia } from 'shared/lib';
import { Spinner } from 'components/Spinner';
import {
  setEditLeaveModalData,
  setLeaveModalMainData,
  toggleVacationModalVisibility,
  downloadAbsenceAttachmentWithoutModal,
} from 'modules/SalaryModule/Vacations/actions';
import {
  getIsoFormatFromDateWithSlashes,
  getTablePaginationRange,
} from 'helpers/salaryHelpers/calendar';
import { EmployeePageType } from 'const/SalaryMainPage';
import { selectEmployeesStatus } from 'modules/SalaryModule/Employees/selectors';
import { useScrollToTop } from 'hooks/useScrollToTop';
import { Button } from 'components/ui';
import { SendInfoButton } from 'components/SalaryComponents/SendInfoButton';
import ResultsTableStyles from 'components/InvoiceSearch/SearchResultsTable/SearchResultsTable.module.css';
import Styles from './CalendarPage.module.css';

const SCREEN_WIDTH_BREAKPOINT_MEDIA = getScreenWidthBreakpointMedia(650);

const { CALENDAR, USERS_NOT_FOUND, DOWNLOAD, YEAR_VIEW_CALENDAR_NO_DATA } =
  SALARY_CALENDAR_TRANSLATIONS;

const { YEAR, MONTH } = HOMEPAGE_FORM_TRANSLATIONS.YEAR_MONTH_SWITCH;

const TABLE_SCROLL_CONFIG = {
  month: { x: 1130, y: 450 },
  year: { x: 1130, y: 1000 },
};

const getEmployeePageInTable = (dataSource, employeeId, pageSize = 20) => {
  const index = dataSource.findIndex((el) => el.employeeid === employeeId);
  return index >= 0 ? Math.floor(index / pageSize) + 1 : 1;
};

export const CalendarPage = memo(() => {
  const dispatch = useDispatch();
  const isMobileVersion = useSimpleMedia(SCREEN_WIDTH_BREAKPOINT_MEDIA);

  const employees = useSelector(selectCalendarEmployees);
  const employeesWithAbsences = useSelector(selectEmployeesWithAbsences);
  const employeesWithAbsencesOnly = useSelector(
    selectEmployeesWithAbsencesOnly
  );
  const yearCalendarEmployee = useSelector(selectYearCalendarEmployee);

  const absences = useSelector(selectAllAbsencesFlat);
  const branchFilter = useSelector(selectCalendarBranchFilter);
  const pagination = useSelector(selectCalendarPagination);

  const monthYearToggle = useSelector(selectMonthYearToggleState);
  const calendarEmployee = useSelector(selectCalendarEmployeeState);
  const withAbsencesOnly = useSelector(selectCalendarWithAbsencesOnly);
  const monthFilter = useSelector(selectCalendarDateFilter);

  const datesArrayForCurrentMonth = useSelector(
    selectDatesArrayForCurrentMonth
  );
  const datesArrayForCurrentYear = useSelector(selectDatesArrayForCurrentYear);
  const year = useSelector(selectCalendarYear);

  const { id: companyId, managedSilae } = useSelector(selectUserCurrentCompany);
  const employeesStatus = useSelector(selectEmployeesStatus);

  useScrollToTop();

  const setMonthYearToggle = useCallback(
    (value) => {
      dispatch(setMonthYearToggleValue({ value }));
    },
    [dispatch]
  );

  const setCalendarEmployee = useCallback(
    (name, value) => {
      dispatch(setCalendarEmployeeValue({ value }));
    },
    [dispatch]
  );

  const setWithAbsencesOnly = useCallback(
    (value) => {
      dispatch(setCalendarWithAbsencesOnly({ value }));
    },
    [dispatch]
  );

  const setMonthFilter = useCallback(
    (date) => {
      dispatch(setCalendarDatePickerValue({ date }));
      dispatch(
        fetchEmployeesRequest({
          pageType: EmployeePageType.Calendar,
          isYearCalendar: monthYearToggle === YEAR,
        })
      );
    },
    [dispatch, monthYearToggle]
  );

  const setCurrentPage = useCallback(
    (page) => {
      dispatch(setCalendarPage(page));
      dispatch(
        fetchEmployeesRequest({
          pageType: EmployeePageType.Calendar,
          isYearCalendar: monthYearToggle === YEAR,
        })
      );
    },
    [dispatch, monthYearToggle]
  );

  useEffect(() => {
    dispatch(
      fetchEmployeesRequest({
        pageType: EmployeePageType.Calendar,
        isYearCalendar: monthYearToggle === YEAR,
      })
    );
  }, [dispatch, monthYearToggle, calendarEmployee]);

  const { isDateHoliday } = useHolidays(year);

  const isLoading = useMemo(
    () =>
      [employeesStatus].some((status) =>
        [STATUS_NOT_REQUESTED, STATUS_LOADING].includes(status)
      ),
    [employeesStatus]
  );

  const isThereNoEmployees = useMemo(() => {
    if (monthYearToggle !== MONTH) return false;

    if (withAbsencesOnly) {
      return employeesWithAbsencesOnly.length === 0;
    }
    return employeesWithAbsences.length === 0;
  }, [
    monthYearToggle,
    withAbsencesOnly,
    employeesWithAbsencesOnly.length,
    employeesWithAbsences.length,
    MONTH,
  ]);

  const tableDataSource = useMemo(
    () =>
      withAbsencesOnly ? employeesWithAbsencesOnly : employeesWithAbsences,
    [withAbsencesOnly, employeesWithAbsencesOnly, employeesWithAbsences]
  );

  const [selectedRowKeys, setSelectedRowKeys] = useState([]);

  const rowSelection = useMemo(
    () => ({
      selectedRowKeys,
      onChange: setSelectedRowKeys,
      onSelectAll: (isSelected) => {
        setSelectedRowKeys(
          isSelected ? tableDataSource.map(({ employeeid }) => employeeid) : []
        );
      },
    }),
    [selectedRowKeys, tableDataSource]
  );

  const openLeaveModal = useCallback(
    (e) => {
      const {
        employeeId,
        leaveType,
        leaveObjId,
        startDate,
        isMorning,
        isAfternoon,
      } = e.target.dataset;

      if (!employeeId) return;

      const employeePageInTable = getEmployeePageInTable(
        tableDataSource,
        employeeId
      );
      const employee =
        employees.find(({ employeeid }) => employeeid === employeeId) || {};

      dispatch(toggleVacationModalVisibility());
      dispatch(
        setLeaveModalMainData({
          employee: formatEmployeeToLeaveModal(employee),
          otherEmployees: employeesWithAbsences,
          selectedEmployeesId: selectedRowKeys,
          type: leaveType,
          startDate: startDate
            ? getIsoFormatFromDateWithSlashes(startDate)
            : null,
          isMorning,
          isAfternoon,
          employeePageInTable,
        })
      );

      if (leaveObjId) {
        dispatch(setEditLeaveModalData({ leaveObjId, submitType: 'edit' }));
      }
    },
    [
      dispatch,
      employees,
      employeesWithAbsences,
      selectedRowKeys,
      tableDataSource,
    ]
  );

  const downloadAttachment = useCallback(
    (e) => {
      const { leaveObjId, employeeId } = e.target.dataset;

      if (!leaveObjId || !employeeId) return;

      dispatch(
        downloadAbsenceAttachmentWithoutModal({
          leaveObjId,
          companyId,
          employeeId,
        })
      );
    },
    [companyId, dispatch]
  );

  const absencesMappedToDates = useMemo(
    () => mapAbsencesToDates({ dates: datesArrayForCurrentMonth, absences }),
    [datesArrayForCurrentMonth, absences]
  );

  const yearAbsencesMappedToDates = useMemo(
    () => mapAbsencesToDates({ dates: datesArrayForCurrentYear, absences }),
    [datesArrayForCurrentYear, absences]
  );

  const handleDownloadAbsences = useCallback(() => {
    const isMonthView = monthYearToggle === MONTH;
    const isYearView = monthYearToggle === YEAR;

    if (isMonthView) {
      dispatch(
        downloadAbsences({
          filter: {
            companyId,
            start: formatDatesToFilter(datesArrayForCurrentMonth.at(0)),
            end: formatDatesToFilter(datesArrayForCurrentMonth.at(-1)),
            branchId: branchFilter,
          },
        })
      );
      return;
    }

    if (isYearView && calendarEmployee) {
      const currentEmployee = employeesWithAbsences.find(
        (employee) => employee.employeeid === calendarEmployee
      );

      if (currentEmployee) {
        dispatch(
          downloadAbsences({
            filter: {
              companyId,
              start: formatDatesToFilter(datesArrayForCurrentYear.at(0)),
              end: formatDatesToFilter(datesArrayForCurrentYear.at(-1)),
              name: `${currentEmployee.firstname1} [${currentEmployee.lastname}]`,
            },
          })
        );
      }
    }
  }, [
    companyId,
    datesArrayForCurrentMonth,
    datesArrayForCurrentYear,
    dispatch,
    employeesWithAbsences,
    monthYearToggle,
    calendarEmployee,
    branchFilter,
    MONTH,
    YEAR,
  ]);

  const monthViewColumns = useGetCalendarMonthViewColumn(
    isMobileVersion,
    datesArrayForCurrentMonth,
    isDateHoliday,
    absencesMappedToDates,
    openLeaveModal,
    downloadAttachment,
    selectedRowKeys
  );

  const yearViewColumns = useGetCalendarYearViewColumn(
    isMobileVersion,
    isDateHoliday,
    openLeaveModal,
    downloadAttachment,
    selectedRowKeys,
    yearAbsencesMappedToDates,
    monthFilter,
    calendarEmployee
  );

  const tablePagination = useMemo(
    () => ({
      showTotal: (total, range) =>
        getTablePaginationRange(range[0], range[1], total),
      responsive: true,
      pageSize: pagination.limit,
      hideOnSinglePage: true,
      size: isMobileVersion ? 'small' : 'default',
      defaultCurrent: 1,
      current: pagination.currentPage,
      onChange: setCurrentPage,
      total: pagination.total,
      showSizeChanger: false,
    }),
    [
      pagination.limit,
      isMobileVersion,
      pagination.currentPage,
      pagination.total,
      setCurrentPage,
    ]
  );

  const tableScroll = useMemo(() => {
    if (monthYearToggle === MONTH && tableDataSource.length) {
      return TABLE_SCROLL_CONFIG.month;
    }
    if (monthYearToggle === YEAR && yearCalendarEmployee?.length) {
      return TABLE_SCROLL_CONFIG.year;
    }
    return undefined;
  }, [monthYearToggle, tableDataSource.length, yearCalendarEmployee]);

  return (
    <div className={Styles.container}>
      <div className={Styles.header}>
        <Typography.Title className={Styles.pageTitle}>
          {CALENDAR}
          <div className={Styles.titleButtonsWrapper}>
            <Button
              sizeAuto
              variant="transparent"
              className={Styles.downloadButton}
              onClick={handleDownloadAbsences}
            >
              <DownloadOutlined className={Styles.downloadIcon} />
              {DOWNLOAD}
            </Button>
            {!isMobileVersion && !managedSilae && (
              <SendInfoButton cancelHide className={Styles.sendInfoButton} />
            )}
          </div>
        </Typography.Title>
        <CalendarFilters
          monthFilter={monthFilter}
          setMonthFilter={setMonthFilter}
          withAbsencesOnly={withAbsencesOnly}
          setWithAbsencesOnly={setWithAbsencesOnly}
          monthYearToggle={monthYearToggle}
          setMonthYearToggle={setMonthYearToggle}
          filteredEmployees={employeesWithAbsences}
          calendarEmployee={calendarEmployee}
          setCalendarEmployee={setCalendarEmployee}
          setCurrentPage={setCurrentPage}
        />
      </div>
      <div className={Styles.contentRow}>
        {isLoading ? (
          <div style={{ marginBottom: '357px' }}>
            <Spinner />
          </div>
        ) : (
          <div className={ResultsTableStyles.resultsTable}>
            {monthYearToggle === MONTH && (
              <Table
                columns={monthViewColumns}
                dataSource={tableDataSource}
                className={clsx(Styles.table, {
                  [Styles.overflowHidden]: isThereNoEmployees,
                })}
                pagination={false}
                scroll={tableScroll}
                rowSelection={rowSelection}
                locale={{ emptyText: USERS_NOT_FOUND }}
                loading={isLoading}
              />
            )}

            {monthYearToggle === YEAR && (
              <Table
                columns={yearViewColumns}
                className={clsx(Styles.table, {
                  [Styles.overflowHidden]: isThereNoEmployees,
                })}
                scroll={tableScroll}
                dataSource={calendarEmployee ? yearCalendarEmployee : undefined}
                pagination={false}
                locale={{ emptyText: YEAR_VIEW_CALENDAR_NO_DATA }}
                loading={isLoading}
              />
            )}
          </div>
        )}
      </div>
      {monthYearToggle === MONTH && (
        <div className={Styles.tablePagination}>
          <Pagination {...tablePagination} />
        </div>
      )}
    </div>
  );
});
